import { fieldValidationMessages } from 'app/constants';
import {
  JSONSchema7,
  JSONSchema7Definition,
  JSONSchema7TypeName,
} from 'json-schema';
import convertToYup, { Config } from 'json-schema-yup-transformer';
import TransformerYup from 'json-schema-yup-transformer/dist/yup/addMethods';
import * as yup from 'yup';
import { cloneDeep, merge } from 'lodash';
import {
  DataTypes,
  SchemaKeywords,
} from 'json-schema-yup-transformer/dist/schema';
import { GenericObject } from 'types/generic.types';

const DEPRECATED_STATUS = 'deprecated';
const EMPTY_YUP_SHAPE = yup.object().shape({});

declare type NodeTypes = SchemaKeywords | DataTypes;
export declare type ConfigErrorTypes = {
  [key in NodeTypes]?: string | ((field: any) => string);
};
export interface ConfigErrors {
  [key: string]: ConfigErrors | ConfigErrorTypes;
}
type SessionTypeName = 'email';
type Field = JSONSchema7 & { sessionType: SessionTypeName };

const ERROR_MESSAGES = {
  defaults: {
    email: fieldValidationMessages.EMAIL,
    number: fieldValidationMessages.TYPE_NUM,
  },
  mixed: {
    required: fieldValidationMessages.REQUIRED,
  },
  email: {
    email: fieldValidationMessages.EMAIL,
    pattern: fieldValidationMessages.EMAIL,
  },
  string: {
    minLength: ([, { minLength }]: [string, { minLength: number }]) =>
      fieldValidationMessages.MIN_CHAR(minLength),
    maxLength: ([, { maxLength }]: [string, { maxLength: number }]) =>
      fieldValidationMessages.MAX_CHAR(maxLength),
  },
  number: {
    min: ([, { min }]: [string, { min: number }]) =>
      fieldValidationMessages.MIN_NUM(min),
    max: ([, { max }]: [string, { max: number }]) =>
      fieldValidationMessages.MAX_NUM(max),
  },
};

const getErrorMessages = (
  type: JSONSchema7TypeName,
  sessionType?: SessionTypeName
) => {
  let typeErrors;
  switch (type) {
    case 'string': {
      typeErrors = {
        ...ERROR_MESSAGES.string,
        ...(sessionType === 'email' ? ERROR_MESSAGES.email : {}),
      };
      break;
    }
    case 'number': {
      typeErrors = ERROR_MESSAGES.number;
      break;
    }
    default: {
      break;
    }
  }
  return { ...ERROR_MESSAGES.mixed, ...typeErrors };
};

const getErrorsForProperties = (properties: JSONSchema7 = {}) => {
  const errors: ConfigErrors = {};
  Object.entries(properties).forEach(([key, field]: [string, Field]) => {
    if (typeof field === 'boolean' || field?.type === undefined) {
      return;
    }

    if (field?.type === 'object') {
      errors[key] = getErrorsForProperties(field.properties);
      return;
    }

    let errorsObject = {};

    if (Array.isArray(field.type)) {
      field.type.forEach((type: JSONSchema7TypeName) => {
        errorsObject = { ...errorsObject, ...getErrorMessages(type) };
      });
    } else {
      errorsObject = getErrorMessages(field.type, field?.sessionType);
    }
    errors[key] = errorsObject;
  });
  return errors;
};

const buildErrorsConfig = (schema: JSONSchema7) => {
  const config: Config = {
    errors: {
      defaults: ERROR_MESSAGES.defaults,
    },
  };
  const errors = getErrorsForProperties(schema.properties);
  return { errors: { ...config.errors, ...errors } };
};

/* eslint-disable no-param-reassign */
const filterOutFields = (
  properties: { [p: string]: JSONSchema7Definition } = {},
  formFields: GenericObject<any>
) => {
  const valuesKeys = Object.keys(formFields);
  return Object.keys(properties)
    .filter((key) => valuesKeys.includes(key))
    .reduce((obj: any, key) => {
      const property = properties[key];

      if (
        typeof property === 'boolean' ||
        property.description === DEPRECATED_STATUS ||
        property.enum
      ) {
        return obj;
      }

      obj[key] = property;

      if (property.type === 'object') {
        const subfields = formFields[key] as GenericObject<any>;
        obj[key].properties = {
          ...filterOutFields(property.properties, subfields),
        };
      }

      return obj;
    }, {});
};
/* eslint-enable no-param-reassign */

const stringCast = (value: any) => {
  if (value === '' || value === undefined) {
    return undefined;
  }
  try {
    return value.toString().trim();
  } catch (e) {
    return value;
  }
};

const numberCast = (value: any) => {
  if (value === '' || value === undefined) {
    return undefined;
  }
  try {
    return Number(value);
  } catch (e) {
    return value;
  }
};

export const transformSchemaToYup = <T extends GenericObject<any>>(
  schema: JSONSchema7,
  formFields: T,
  errorsConfig?: { errors: { [p: string]: ConfigErrors | ConfigErrorTypes } }
) => {
  if (!schema || !schema.properties) {
    return EMPTY_YUP_SHAPE;
  }

  const clonedSchema = cloneDeep(schema);

  // eslint-disable-next-line no-param-reassign
  clonedSchema.properties = filterOutFields(
    clonedSchema.properties,
    formFields
  );

  const globalErrors = buildErrorsConfig(clonedSchema);
  const config = merge(globalErrors, errorsConfig);

  TransformerYup.addMethod(TransformerYup.string, 'cast', stringCast);
  TransformerYup.addMethod(TransformerYup.number, 'cast', numberCast);

  return convertToYup(clonedSchema, config as Config) || EMPTY_YUP_SHAPE;
};
